import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { UserPlus, Pencil, Trash2, ShieldCheck, User, LockKeyhole } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { ADMIN_MODULES } from "@/lib/adminModules";

const emptyUser = { email: "", name: "", password: "", role: "staff", phone: "", can_trend_radar: false, allowed_modules: [] };

const AdminUsers = () => {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/users/staff");
      setUsers(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...editing };
      if (!payload.password) delete payload.password;
      if (editing.id) {
        await api.patch(`/users/staff/${editing.id}`, payload);
        toast.success("Kullanıcı güncellendi");
      } else {
        await api.post("/users/staff", payload);
        toast.success("Personel eklendi");
      }
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const remove = async (u) => {
    try {
      await api.delete(`/users/staff/${u.id}`);
      toast.success("Silindi");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const roleBadge = (r) => r === "admin" ? (
    <Badge className="bg-amber-100 text-amber-700 border-amber-300 gap-1">
      <ShieldCheck className="w-3 h-3" /> Yönetici
    </Badge>
  ) : (
    <Badge className="bg-slate-100 text-slate-700 border-slate-300 gap-1">
      <User className="w-3 h-3" /> Personel
    </Badge>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight" data-testid="admin-users-title">Personel Hesapları</h1>
          <p className="text-sm text-slate-500 mt-1">
            Yönetici (admin) tüm sistemi görür. Personel yalnızca günlük nakit girişi yapabilir ve kasa devrini kaydeder.
          </p>
        </div>
        <Button
          data-testid="user-add-btn"
          onClick={() => setEditing({ ...emptyUser })}
          className="bg-slate-900 hover:bg-slate-800"
        >
          <UserPlus className="w-4 h-4 mr-2" /> Yeni Personel
        </Button>
      </div>

      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="text-base font-semibold">Kayıtlı Personel & Yöneticiler</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Ad Soyad</TableHead>
                <TableHead>E-posta</TableHead>
                <TableHead>Telefon</TableHead>
                <TableHead>Rol</TableHead>
                <TableHead className="text-right">Aksiyon</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.length === 0 && (
                <TableRow><TableCell colSpan={5} className="text-center py-8 text-slate-500">Kayıt yok.</TableCell></TableRow>
              )}
              {users.map((u) => (
                <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                  <TableCell className="font-medium">
                    {u.name}
                    {u.id === me?.id && <span className="ml-2 text-xs text-slate-400">(siz)</span>}
                  </TableCell>
                  <TableCell className="text-slate-600">{u.email}</TableCell>
                  <TableCell className="text-slate-600">{u.phone || "—"}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap items-center gap-1">
                      {roleBadge(u.role)}
                      {u.role === "staff" && u.can_trend_radar && (
                        <Badge className="bg-slate-900 text-white gap-1 text-[10px]" data-testid={`user-radar-badge-${u.id}`}>Radar</Badge>
                      )}
                      {u.role === "staff" && Array.isArray(u.allowed_modules) && u.allowed_modules.length > 0 && (
                        <Badge className="bg-indigo-100 text-indigo-700 border-indigo-200 gap-1 text-[10px]" data-testid={`user-modules-badge-${u.id}`}>
                          {u.allowed_modules.length} panel
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-right space-x-1">
                    <Button size="sm" variant="outline" onClick={() => setEditing({ ...u, password: "" })} data-testid={`user-edit-${u.id}`}>
                      <Pencil className="w-3 h-3" />
                    </Button>
                    {u.id !== me?.id && (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button size="sm" variant="destructive" data-testid={`user-delete-${u.id}`}>
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>{u.name} silinsin mi?</AlertDialogTitle>
                            <AlertDialogDescription>
                              Bu kullanıcı silinecek ve bir daha giriş yapamayacak. Bu işlem geri alınamaz.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Vazgeç</AlertDialogCancel>
                            <AlertDialogAction onClick={() => remove(u)} className="bg-red-600 hover:bg-red-700">Sil</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent data-testid="user-dialog">
          <DialogHeader>
            <DialogTitle>{editing?.id ? "Personel Düzenle" : "Yeni Personel Ekle"}</DialogTitle>
            <DialogDescription>
              Şifre alanını boş bırakırsanız mevcut şifre korunur (düzenlemede). Yeni kullanıcıda zorunludur.
            </DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-sm font-semibold text-slate-900 mb-1 block">Ad Soyad</Label>
                  <Input data-testid="user-name" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} placeholder="Ali Yılmaz" />
                </div>
                <div>
                  <Label className="text-sm font-semibold text-slate-900 mb-1 block">E-posta</Label>
                  <Input data-testid="user-email" type="email" value={editing.email} onChange={(e) => setEditing({ ...editing, email: e.target.value })} disabled={!!editing.id} placeholder="ali@fotuber.com.tr" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-sm font-semibold text-slate-900 mb-1 block">Telefon</Label>
                  <Input data-testid="user-phone" value={editing.phone || ""} onChange={(e) => setEditing({ ...editing, phone: e.target.value })} placeholder="0501..." />
                </div>
                <div>
                  <Label className="text-sm font-semibold text-slate-900 mb-1 block">Rol</Label>
                  <Select value={editing.role} onValueChange={(v) => setEditing({ ...editing, role: v })}>
                    <SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="staff">Personel (Sadece günlük nakit)</SelectItem>
                      <SelectItem value="admin">Yönetici (Tüm yetkiler)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-sm font-semibold text-slate-900 mb-1 block">
                  {editing.id ? "Yeni Şifre (boş bırakırsanız mevcut korunur)" : "Şifre"}
                </Label>
                <Input
                  data-testid="user-password"
                  type="text"
                  value={editing.password || ""}
                  onChange={(e) => setEditing({ ...editing, password: e.target.value })}
                  placeholder="En az 6 karakter"
                />
              </div>

              {/* Yetkiler */}
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                <div className="text-sm font-semibold text-slate-900 mb-2 flex items-center gap-1.5">
                  <LockKeyhole className="w-3.5 h-3.5" /> Yetkiler
                </div>
                {editing.role === "admin" ? (
                  <p className="text-xs text-slate-500">Yöneticiler tüm panellere ve yetkilere sahiptir.</p>
                ) : (
                  <>
                    <label className="flex items-start gap-3 cursor-pointer mb-3" data-testid="perm-trend-radar-wrap">
                      <input
                        type="checkbox"
                        data-testid="perm-trend-radar"
                        checked={!!editing.can_trend_radar}
                        onChange={(e) => setEditing({ ...editing, can_trend_radar: e.target.checked })}
                        className="mt-0.5 w-4 h-4 accent-slate-900"
                      />
                      <span>
                        <span className="text-sm font-medium text-slate-800">Sektör Radarı</span>
                        <span className="block text-xs text-slate-500">Genel Bakış'taki radar panosunu görür ve yeniler.</span>
                      </span>
                    </label>

                    <div className="border-t border-slate-200 pt-3">
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Erişebileceği Paneller</div>
                        <div className="flex gap-2">
                          <button
                            type="button"
                            data-testid="perm-select-all"
                            onClick={() => setEditing({ ...editing, allowed_modules: ADMIN_MODULES.map((m) => m.key) })}
                            className="text-[11px] px-2 py-1 rounded bg-slate-900 text-white hover:bg-slate-800"
                          >Tümünü Seç</button>
                          <button
                            type="button"
                            data-testid="perm-clear-all"
                            onClick={() => setEditing({ ...editing, allowed_modules: [] })}
                            className="text-[11px] px-2 py-1 rounded border border-slate-300 text-slate-700 hover:bg-white"
                          >Temizle</button>
                        </div>
                      </div>
                      <p className="text-[11px] text-slate-500 mb-2">Seçilen modüller personele giriş yaptığında sol menüde görünür. Yönetici her zaman tüm panellere erişir.</p>
                      {Object.entries(
                        ADMIN_MODULES.reduce((acc, m) => {
                          const g = m.group || "Diğer";
                          (acc[g] = acc[g] || []).push(m);
                          return acc;
                        }, {})
                      ).map(([groupName, mods]) => (
                        <div key={groupName} className="mt-2">
                          <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">{groupName}</div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                            {mods.map((m) => {
                              const checked = (editing.allowed_modules || []).includes(m.key);
                              return (
                                <label
                                  key={m.key}
                                  data-testid={`perm-module-${m.key}`}
                                  className={`flex items-center gap-2 px-2 py-1.5 rounded-md cursor-pointer text-xs ${checked ? "bg-indigo-50 border border-indigo-200 text-indigo-900" : "bg-white border border-slate-200 text-slate-700 hover:border-slate-300"}`}
                                >
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={(e) => {
                                      const cur = new Set(editing.allowed_modules || []);
                                      if (e.target.checked) cur.add(m.key); else cur.delete(m.key);
                                      setEditing({ ...editing, allowed_modules: Array.from(cur) });
                                    }}
                                    className="w-3.5 h-3.5 accent-indigo-600"
                                  />
                                  <span className="truncate">{m.label}</span>
                                </label>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Vazgeç</Button>
            <Button data-testid="user-save-btn" onClick={save} disabled={saving} className="bg-slate-900 hover:bg-slate-800">
              {saving ? "Kaydediliyor..." : "Kaydet"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminUsers;
